import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const TOKEN_KEY = "erio_token";

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Session-expired interceptor. When an authed API call returns 401 (token
// expired / revoked), drop the local JWT, capture the user's current
// in-app path (so we can resume after re-login), and bounce to /login with
// a session_expired flag. Public endpoints that legitimately 401 (e.g.
// rate-limited /auth/login) opt out by not having a token attached.
//
// Why path + flag (not just a flag): on long board-pack reviews CROs lose
// context if we drop them on /dashboard. Resume-where-you-left-off is a
// small touch but matters for the pilot demos.
api.interceptors.response.use(
  (resp) => resp,
  (err) => {
    try {
      const status = err?.response?.status;
      const url = err?.config?.url || "";
      // Don't trigger on the login POST itself — that's a bad-password
      // 401, not a session-expiry. Same for /auth/me probes that fire
      // before a user is ever signed in.
      const isAuthEndpoint = /\/auth\/(login|mfa\/|magic-link\/|google\/|me)/.test(url);
      if (status === 401 && !isAuthEndpoint && localStorage.getItem(TOKEN_KEY)) {
        localStorage.removeItem(TOKEN_KEY);
        try {
          const here = window.location.pathname + window.location.search + window.location.hash;
          // Avoid storing /login as the "next" — that'd loop.
          if (here && !here.startsWith("/login")) {
            sessionStorage.setItem("erio_post_login_redirect", here);
          }
        } catch (_e) { /* private mode */ }
        // Hard nav so any in-flight stale-token requests are cancelled.
        if (!window.location.pathname.startsWith("/login")) {
          window.location.replace("/login?session_expired=1");
        }
      }
    } catch (_e) { /* never throw from an interceptor */ }
    return Promise.reject(err);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
